import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Phone } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import emailjs from '@emailjs/browser';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// EmailJS Configuration (Public - Safe to expose)
const EMAILJS_SERVICE_ID = 'service_vqrp2do';
const EMAILJS_TEMPLATE_ID = 'template_job_application';
const EMAILJS_PUBLIC_KEY = 'YOUR_PUBLIC_KEY'; // Will be replaced with actual key

const Home = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    sex: '',
    email: '',
    name: '',
    phoneNumber: '',
    age: '',
    dressSize: '',
    nationality: '',
    adultExperience: '',
    location: '',
    workType: ''
  });

  const sendEmailNotification = async (data) => {
    try {
      // Send email using EmailJS
      const templateParams = {
        to_email: 'sam@gwm.com.au',
        from_name: data.name,
        from_email: data.email,
        phone_number: data.phoneNumber || 'N/A',
        age: data.age || 'N/A',
        dress_size: data.dressSize || 'N/A',
        nationality: data.nationality || 'N/A',
        adult_experience: data.adultExperience || 'N/A',
        location: data.location || 'N/A',
        work_type: data.workType || 'N/A',
        submission_date: new Date().toLocaleString(),
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      console.log('Email sent successfully via EmailJS');
    } catch (error) {
      console.error('EmailJS error:', error);
      // Don't throw - we still want to save to database
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields are filled
    const requiredFields = ['sex', 'email', 'name', 'phoneNumber', 'age', 'dressSize', 'nationality', 'adultExperience', 'location', 'workType'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields before submitting.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Save to database
      const response = await axios.post(`${BACKEND_URL}/api/applications`, formData);
      
      // Send email notification
      await sendEmailNotification(formData);
      
      // Fire Google Ads conversion tracking
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17392580938',
          'event_category': 'form_submission',
          'event_label': 'job_application'
        });
      }
      
      toast({
        title: "Application Submitted Successfully!",
        description: "We'll be in touch soon. Thank you for your interest!",
      });
      
      // Reset form
      setFormData({
        sex: '',
        email: '',
        name: '',
        phoneNumber: '',
        age: '',
        dressSize: '',
        nationality: '',
        adultExperience: '',
        location: '',
        workType: ''
      });
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-black text-white py-3 px-4 shadow-lg">
        <div className="container mx-auto max-w-7xl">
          {/* Mobile Layout */}
          <div className="flex flex-col md:hidden items-center gap-2">
            <img 
              src="https://d9hhrg4mnvzow.cloudfront.net/www.highpaidnoexperiencejobs.com.au/82372698-nehpj_105a02v000000000000028.png" 
              alt="No Experience High Paying Jobs" 
              className="h-10"
            />
            <a 
              href="mailto:info@anytimelawyers.com"
              className="flex items-center gap-2 text-sm"
            >
              <Mail className="w-4 h-4" />
              info@anytimelawyers.com
            </a>
          </div>
          
          {/* Desktop Layout */}
          <div className="hidden md:flex justify-between items-center">
            <div className="flex items-center">
              <img 
                src="https://d9hhrg4mnvzow.cloudfront.net/www.highpaidnoexperiencejobs.com.au/82372698-nehpj_105a02v000000000000028.png" 
                alt="No Experience High Paying Jobs" 
                className="h-12"
              />
            </div>
            
            <div className="flex gap-4">
              <a 
                href="tel:0429085683"
                className="flex items-center gap-2 px-6 py-2 border-2 rounded-full hover:text-white transition-all font-semibold"
                style={{ 
                  borderColor: 'rgb(242, 6, 95)', 
                  color: 'rgb(242, 6, 95)',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgb(242, 6, 95)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                SMS Lisa: 0429 085683
              </a>
              <a 
                href="mailto:info@anytimelawyers.com"
                className="flex items-center gap-2 px-6 py-2 border-2 border-white rounded-full hover:bg-white hover:text-black transition-all font-semibold"
              >
                <Mail className="w-5 h-5" />
                info@anytimelawyers.com
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Background Image and Form */}
      <section className="relative px-4 overflow-hidden flex items-center" style={{ minHeight: '85vh' }}>
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_hpnej-onepage/artifacts/nhwn7ehs_image.png')",
          }}
        />
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/60" />
        
        {/* Private Jet Image at Bottom */}
        <div className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none hidden md:block">
          <img 
            src="https://customer-assets.emergentagent.com/job_hpnej-onepage/artifacts/kbthl54l_56024ff3-195445_1000000000000000000028.png" 
            alt="Private Jet" 
            className="w-full max-w-4xl mx-auto"
            style={{ maxHeight: '350px', objectFit: 'contain' }}
          />
        </div>
        
        {/* Content */}
        <div className="container mx-auto max-w-7xl relative z-20 h-full flex items-center py-20">
          <div className="grid lg:grid-cols-2 gap-8 items-center w-full">
            {/* Left Side - Text Content */}
            <div className="text-white text-center lg:text-left">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 lg:mb-6 leading-tight">
                LOOKING FOR NO EXPERIENCE WORK IN SYDNEY & MELBOURNE?
              </h1>
              <p className="text-xl md:text-2xl lg:text-3xl font-semibold mb-6 lg:mb-8">JOIN OUR 5 STAR ESTABLISHMENTS</p>
              
              <div className="mb-6">
                <p className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2" style={{ color: 'rgb(242, 6, 95)' }}>INCOME POTENTIAL</p>
                <p className="text-4xl md:text-5xl lg:text-6xl font-black" style={{ color: 'rgb(242, 6, 95)' }}>
                  $3,000 – $8,000 PER WEEK
                </p>
              </div>
              
              <div className="space-y-2 text-lg md:text-xl">
                <p className="flex items-center gap-3 justify-center lg:justify-start">
                  <span className="text-xl md:text-2xl font-bold" style={{ color: 'rgb(242, 6, 95)' }}>-</span>
                  Full Training Will Be Provided.
                </p>
                <p className="flex items-center gap-3 justify-center lg:justify-start">
                  <span className="text-xl md:text-2xl font-bold" style={{ color: 'rgb(242, 6, 95)' }}>-</span>
                  Friendly Management
                </p>
                <p className="flex items-center gap-3 justify-center lg:justify-start">
                  <span className="text-xl md:text-2xl font-bold" style={{ color: 'rgb(242, 6, 95)' }}>-</span>
                  No Experience Required
                </p>
              </div>
              
              {/* Mobile: Show JOBS text */}
              <div className="mt-8 lg:hidden">
                <p className="text-6xl font-black" style={{ color: 'rgb(242, 6, 95)' }}>JOBS</p>
              </div>
            </div>

            {/* Right Side - Application Form */}
            <div className="bg-black/60 backdrop-blur-md rounded-3xl p-6 shadow-2xl max-w-md mx-auto lg:ml-auto lg:mr-0 border border-white/10">
              <h2 className="text-2xl font-bold text-center mb-2" style={{ color: 'rgb(242, 6, 95)' }}>
                CONFIDENTIAL APPLICATION
              </h2>
              <p className="text-center text-base font-semibold mb-6 underline" style={{ color: 'rgb(242, 6, 95)' }}>
                APPLY FOR LEGAL JOBS
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Sex Field - First */}
                <div className="flex items-center gap-4 h-11 bg-white/20 border border-white/30 rounded-full px-5">
                  <span className="text-white text-sm font-semibold">Sex*:</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sex"
                      value="M"
                      checked={formData.sex === 'M'}
                      onChange={(e) => handleChange('sex', e.target.value)}
                      className="w-4 h-4 accent-pink-600"
                      required
                    />
                    <span className="text-white text-sm">M</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="sex"
                      value="F"
                      checked={formData.sex === 'F'}
                      onChange={(e) => handleChange('sex', e.target.value)}
                      className="w-4 h-4 accent-pink-600"
                      required
                    />
                    <span className="text-white text-sm">F</span>
                  </label>
                </div>

                <Input
                  type="email"
                  placeholder="Email*"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="h-11 bg-white/20 border border-white/30 rounded-full px-5 placeholder:text-gray-300 text-sm text-white focus:bg-white/25 focus:border-pink-500"
                  required
                />

                <Input
                  type="text"
                  placeholder="Name*"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="h-11 bg-white/20 border border-white/30 rounded-full px-5 placeholder:text-gray-300 text-sm text-white focus:bg-white/25 focus:border-pink-500"
                  required
                />

                <Input
                  type="tel"
                  placeholder="Phone Number"
                  value={formData.phoneNumber}
                  onChange={(e) => handleChange('phoneNumber', e.target.value)}
                  className="h-11 bg-white/20 border border-white/30 rounded-full px-5 placeholder:text-gray-300 text-sm text-white focus:bg-white/25 focus:border-pink-500"
                />

                <Select value={formData.age} onValueChange={(value) => handleChange('age', value)} required>
                  <SelectTrigger className="h-11 bg-white/20 border border-white/30 rounded-full px-5 text-gray-200 text-sm focus:bg-white/25 focus:border-pink-500">
                    <SelectValue placeholder="Age*" />
                  </SelectTrigger>
                  <SelectContent>
                    {[18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30].map((age) => (
                      <SelectItem key={age} value={String(age)}>{age}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={formData.dressSize} onValueChange={(value) => handleChange('dressSize', value)} required>
                  <SelectTrigger className="h-11 bg-white/20 border border-white/30 rounded-full px-5 text-gray-200 text-sm focus:bg-white/25 focus:border-pink-500">
                    <SelectValue placeholder="Dress Size*" />
                  </SelectTrigger>
                  <SelectContent>
                    {['6', '8', '10', '12', '14', '16'].map((size) => (
                      <SelectItem key={size} value={size}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={formData.nationality} onValueChange={(value) => handleChange('nationality', value)} required>
                  <SelectTrigger className="h-11 bg-white/20 border border-white/30 rounded-full px-5 text-gray-200 text-sm focus:bg-white/25 focus:border-pink-500">
                    <SelectValue placeholder="Nationality*" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="Afghanistan">Afghanistan</SelectItem>
                    <SelectItem value="Albania">Albania</SelectItem>
                    <SelectItem value="Algeria">Algeria</SelectItem>
                    <SelectItem value="Andorra">Andorra</SelectItem>
                    <SelectItem value="Angola">Angola</SelectItem>
                    <SelectItem value="Argentina">Argentina</SelectItem>
                    <SelectItem value="Armenia">Armenia</SelectItem>
                    <SelectItem value="Australia">Australia</SelectItem>
                    <SelectItem value="Austria">Austria</SelectItem>
                    <SelectItem value="Azerbaijan">Azerbaijan</SelectItem>
                    <SelectItem value="Bahamas">Bahamas</SelectItem>
                    <SelectItem value="Bahrain">Bahrain</SelectItem>
                    <SelectItem value="Bangladesh">Bangladesh</SelectItem>
                    <SelectItem value="Barbados">Barbados</SelectItem>
                    <SelectItem value="Belarus">Belarus</SelectItem>
                    <SelectItem value="Belgium">Belgium</SelectItem>
                    <SelectItem value="Belize">Belize</SelectItem>
                    <SelectItem value="Benin">Benin</SelectItem>
                    <SelectItem value="Bhutan">Bhutan</SelectItem>
                    <SelectItem value="Bolivia">Bolivia</SelectItem>
                    <SelectItem value="Bosnia and Herzegovina">Bosnia and Herzegovina</SelectItem>
                    <SelectItem value="Botswana">Botswana</SelectItem>
                    <SelectItem value="Brazil">Brazil</SelectItem>
                    <SelectItem value="Brunei">Brunei</SelectItem>
                    <SelectItem value="Bulgaria">Bulgaria</SelectItem>
                    <SelectItem value="Burkina Faso">Burkina Faso</SelectItem>
                    <SelectItem value="Burundi">Burundi</SelectItem>
                    <SelectItem value="Cambodia">Cambodia</SelectItem>
                    <SelectItem value="Cameroon">Cameroon</SelectItem>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="Cape Verde">Cape Verde</SelectItem>
                    <SelectItem value="Central African Republic">Central African Republic</SelectItem>
                    <SelectItem value="Chad">Chad</SelectItem>
                    <SelectItem value="Chile">Chile</SelectItem>
                    <SelectItem value="China">China</SelectItem>
                    <SelectItem value="Colombia">Colombia</SelectItem>
                    <SelectItem value="Comoros">Comoros</SelectItem>
                    <SelectItem value="Congo">Congo</SelectItem>
                    <SelectItem value="Costa Rica">Costa Rica</SelectItem>
                    <SelectItem value="Croatia">Croatia</SelectItem>
                    <SelectItem value="Cuba">Cuba</SelectItem>
                    <SelectItem value="Cyprus">Cyprus</SelectItem>
                    <SelectItem value="Czech Republic">Czech Republic</SelectItem>
                    <SelectItem value="Denmark">Denmark</SelectItem>
                    <SelectItem value="Djibouti">Djibouti</SelectItem>
                    <SelectItem value="Dominica">Dominica</SelectItem>
                    <SelectItem value="Dominican Republic">Dominican Republic</SelectItem>
                    <SelectItem value="Ecuador">Ecuador</SelectItem>
                    <SelectItem value="Egypt">Egypt</SelectItem>
                    <SelectItem value="El Salvador">El Salvador</SelectItem>
                    <SelectItem value="Equatorial Guinea">Equatorial Guinea</SelectItem>
                    <SelectItem value="Eritrea">Eritrea</SelectItem>
                    <SelectItem value="Estonia">Estonia</SelectItem>
                    <SelectItem value="Ethiopia">Ethiopia</SelectItem>
                    <SelectItem value="Fiji">Fiji</SelectItem>
                    <SelectItem value="Finland">Finland</SelectItem>
                    <SelectItem value="France">France</SelectItem>
                    <SelectItem value="Gabon">Gabon</SelectItem>
                    <SelectItem value="Gambia">Gambia</SelectItem>
                    <SelectItem value="Georgia">Georgia</SelectItem>
                    <SelectItem value="Germany">Germany</SelectItem>
                    <SelectItem value="Ghana">Ghana</SelectItem>
                    <SelectItem value="Greece">Greece</SelectItem>
                    <SelectItem value="Grenada">Grenada</SelectItem>
                    <SelectItem value="Guatemala">Guatemala</SelectItem>
                    <SelectItem value="Guinea">Guinea</SelectItem>
                    <SelectItem value="Guinea-Bissau">Guinea-Bissau</SelectItem>
                    <SelectItem value="Guyana">Guyana</SelectItem>
                    <SelectItem value="Haiti">Haiti</SelectItem>
                    <SelectItem value="Honduras">Honduras</SelectItem>
                    <SelectItem value="Hungary">Hungary</SelectItem>
                    <SelectItem value="Iceland">Iceland</SelectItem>
                    <SelectItem value="India">India</SelectItem>
                    <SelectItem value="Indonesia">Indonesia</SelectItem>
                    <SelectItem value="Iran">Iran</SelectItem>
                    <SelectItem value="Iraq">Iraq</SelectItem>
                    <SelectItem value="Ireland">Ireland</SelectItem>
                    <SelectItem value="Israel">Israel</SelectItem>
                    <SelectItem value="Italy">Italy</SelectItem>
                    <SelectItem value="Jamaica">Jamaica</SelectItem>
                    <SelectItem value="Japan">Japan</SelectItem>
                    <SelectItem value="Jordan">Jordan</SelectItem>
                    <SelectItem value="Kazakhstan">Kazakhstan</SelectItem>
                    <SelectItem value="Kenya">Kenya</SelectItem>
                    <SelectItem value="Kiribati">Kiribati</SelectItem>
                    <SelectItem value="Korea, North">Korea, North</SelectItem>
                    <SelectItem value="Korea, South">Korea, South</SelectItem>
                    <SelectItem value="Kuwait">Kuwait</SelectItem>
                    <SelectItem value="Kyrgyzstan">Kyrgyzstan</SelectItem>
                    <SelectItem value="Laos">Laos</SelectItem>
                    <SelectItem value="Latvia">Latvia</SelectItem>
                    <SelectItem value="Lebanon">Lebanon</SelectItem>
                    <SelectItem value="Lesotho">Lesotho</SelectItem>
                    <SelectItem value="Liberia">Liberia</SelectItem>
                    <SelectItem value="Libya">Libya</SelectItem>
                    <SelectItem value="Liechtenstein">Liechtenstein</SelectItem>
                    <SelectItem value="Lithuania">Lithuania</SelectItem>
                    <SelectItem value="Luxembourg">Luxembourg</SelectItem>
                    <SelectItem value="Madagascar">Madagascar</SelectItem>
                    <SelectItem value="Malawi">Malawi</SelectItem>
                    <SelectItem value="Malaysia">Malaysia</SelectItem>
                    <SelectItem value="Maldives">Maldives</SelectItem>
                    <SelectItem value="Mali">Mali</SelectItem>
                    <SelectItem value="Malta">Malta</SelectItem>
                    <SelectItem value="Marshall Islands">Marshall Islands</SelectItem>
                    <SelectItem value="Mauritania">Mauritania</SelectItem>
                    <SelectItem value="Mauritius">Mauritius</SelectItem>
                    <SelectItem value="Mexico">Mexico</SelectItem>
                    <SelectItem value="Micronesia">Micronesia</SelectItem>
                    <SelectItem value="Moldova">Moldova</SelectItem>
                    <SelectItem value="Monaco">Monaco</SelectItem>
                    <SelectItem value="Mongolia">Mongolia</SelectItem>
                    <SelectItem value="Montenegro">Montenegro</SelectItem>
                    <SelectItem value="Morocco">Morocco</SelectItem>
                    <SelectItem value="Mozambique">Mozambique</SelectItem>
                    <SelectItem value="Myanmar">Myanmar</SelectItem>
                    <SelectItem value="Namibia">Namibia</SelectItem>
                    <SelectItem value="Nauru">Nauru</SelectItem>
                    <SelectItem value="Nepal">Nepal</SelectItem>
                    <SelectItem value="Netherlands">Netherlands</SelectItem>
                    <SelectItem value="New Zealand">New Zealand</SelectItem>
                    <SelectItem value="Nicaragua">Nicaragua</SelectItem>
                    <SelectItem value="Niger">Niger</SelectItem>
                    <SelectItem value="Nigeria">Nigeria</SelectItem>
                    <SelectItem value="Norway">Norway</SelectItem>
                    <SelectItem value="Oman">Oman</SelectItem>
                    <SelectItem value="Pakistan">Pakistan</SelectItem>
                    <SelectItem value="Palau">Palau</SelectItem>
                    <SelectItem value="Panama">Panama</SelectItem>
                    <SelectItem value="Papua New Guinea">Papua New Guinea</SelectItem>
                    <SelectItem value="Paraguay">Paraguay</SelectItem>
                    <SelectItem value="Peru">Peru</SelectItem>
                    <SelectItem value="Philippines">Philippines</SelectItem>
                    <SelectItem value="Poland">Poland</SelectItem>
                    <SelectItem value="Portugal">Portugal</SelectItem>
                    <SelectItem value="Qatar">Qatar</SelectItem>
                    <SelectItem value="Romania">Romania</SelectItem>
                    <SelectItem value="Russia">Russia</SelectItem>
                    <SelectItem value="Rwanda">Rwanda</SelectItem>
                    <SelectItem value="Saint Kitts and Nevis">Saint Kitts and Nevis</SelectItem>
                    <SelectItem value="Saint Lucia">Saint Lucia</SelectItem>
                    <SelectItem value="Saint Vincent">Saint Vincent</SelectItem>
                    <SelectItem value="Samoa">Samoa</SelectItem>
                    <SelectItem value="San Marino">San Marino</SelectItem>
                    <SelectItem value="Sao Tome and Principe">Sao Tome and Principe</SelectItem>
                    <SelectItem value="Saudi Arabia">Saudi Arabia</SelectItem>
                    <SelectItem value="Senegal">Senegal</SelectItem>
                    <SelectItem value="Serbia">Serbia</SelectItem>
                    <SelectItem value="Seychelles">Seychelles</SelectItem>
                    <SelectItem value="Sierra Leone">Sierra Leone</SelectItem>
                    <SelectItem value="Singapore">Singapore</SelectItem>
                    <SelectItem value="Slovakia">Slovakia</SelectItem>
                    <SelectItem value="Slovenia">Slovenia</SelectItem>
                    <SelectItem value="Solomon Islands">Solomon Islands</SelectItem>
                    <SelectItem value="Somalia">Somalia</SelectItem>
                    <SelectItem value="South Africa">South Africa</SelectItem>
                    <SelectItem value="South Sudan">South Sudan</SelectItem>
                    <SelectItem value="Spain">Spain</SelectItem>
                    <SelectItem value="Sri Lanka">Sri Lanka</SelectItem>
                    <SelectItem value="Sudan">Sudan</SelectItem>
                    <SelectItem value="Suriname">Suriname</SelectItem>
                    <SelectItem value="Swaziland">Swaziland</SelectItem>
                    <SelectItem value="Sweden">Sweden</SelectItem>
                    <SelectItem value="Switzerland">Switzerland</SelectItem>
                    <SelectItem value="Syria">Syria</SelectItem>
                    <SelectItem value="Taiwan">Taiwan</SelectItem>
                    <SelectItem value="Tajikistan">Tajikistan</SelectItem>
                    <SelectItem value="Tanzania">Tanzania</SelectItem>
                    <SelectItem value="Thailand">Thailand</SelectItem>
                    <SelectItem value="Timor-Leste">Timor-Leste</SelectItem>
                    <SelectItem value="Togo">Togo</SelectItem>
                    <SelectItem value="Tonga">Tonga</SelectItem>
                    <SelectItem value="Trinidad and Tobago">Trinidad and Tobago</SelectItem>
                    <SelectItem value="Tunisia">Tunisia</SelectItem>
                    <SelectItem value="Turkey">Turkey</SelectItem>
                    <SelectItem value="Turkmenistan">Turkmenistan</SelectItem>
                    <SelectItem value="Tuvalu">Tuvalu</SelectItem>
                    <SelectItem value="Uganda">Uganda</SelectItem>
                    <SelectItem value="Ukraine">Ukraine</SelectItem>
                    <SelectItem value="United Arab Emirates">United Arab Emirates</SelectItem>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="Uruguay">Uruguay</SelectItem>
                    <SelectItem value="Uzbekistan">Uzbekistan</SelectItem>
                    <SelectItem value="Vanuatu">Vanuatu</SelectItem>
                    <SelectItem value="Vatican City">Vatican City</SelectItem>
                    <SelectItem value="Venezuela">Venezuela</SelectItem>
                    <SelectItem value="Vietnam">Vietnam</SelectItem>
                    <SelectItem value="Yemen">Yemen</SelectItem>
                    <SelectItem value="Zambia">Zambia</SelectItem>
                    <SelectItem value="Zimbabwe">Zimbabwe</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={formData.adultExperience} onValueChange={(value) => handleChange('adultExperience', value)} required>
                  <SelectTrigger className="h-11 bg-white/20 border border-white/30 rounded-full px-5 text-gray-200 text-sm focus:bg-white/25 focus:border-pink-500">
                    <SelectValue placeholder="Adult Industry Experience*" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={formData.location} onValueChange={(value) => handleChange('location', value)} required>
                  <SelectTrigger className="h-11 bg-white/20 border border-white/30 rounded-full px-5 text-gray-200 text-sm focus:bg-white/25 focus:border-pink-500">
                    <SelectValue placeholder="Location (Sydney/Melbourne)*" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sydney">Sydney</SelectItem>
                    <SelectItem value="Melbourne">Melbourne</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={formData.workType} onValueChange={(value) => handleChange('workType', value)} required>
                  <SelectTrigger className="h-11 bg-white/20 border border-white/30 rounded-full px-5 text-gray-200 text-sm focus:bg-white/25 focus:border-pink-500">
                    <SelectValue placeholder="What type of adult work have you done?*" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stripping">1. Stripping/Dancing</SelectItem>
                    <SelectItem value="fullservice">2. Full Service</SelectItem>
                    <SelectItem value="onlyfans">3. Only Fans Model</SelectItem>
                    <SelectItem value="noexperience">4. No adult work experience, but I'm interested.</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  type="tel"
                  placeholder="Phone Number*"
                  value={formData.phoneNumber}
                  onChange={(e) => handleChange('phoneNumber', e.target.value)}
                  className="h-11 bg-white/20 border border-white/30 rounded-full px-5 placeholder:text-gray-300 text-sm text-white focus:bg-white/25 focus:border-pink-500"
                  required
                />

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full h-12 bg-transparent hover:text-white font-bold text-lg rounded-full transition-all duration-300 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ 
                    borderWidth: '3px',
                    borderColor: 'rgb(242, 6, 95)',
                    color: 'rgb(242, 6, 95)',
                  }}
                  onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.backgroundColor = 'rgb(242, 6, 95)')}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  {isSubmitting ? 'SUBMITTING...' : 'APPLY FOR JOBS NOW'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section - Dark Redesign */}
      <section className="py-16 px-4 bg-black">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Free Accommodation */}
            <div className="group relative overflow-hidden rounded-2xl p-8 text-center transition-all duration-300 bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 hover:border-pink-600">
              <div className="mb-6">
                <div className="text-6xl font-black text-transparent bg-clip-text mb-2" 
                  style={{ backgroundImage: 'linear-gradient(135deg, rgb(242, 6, 95), rgb(200, 5, 80))' }}>
                  FREE
                </div>
                <div className="h-1 w-24 mx-auto rounded-full" 
                  style={{ background: 'linear-gradient(90deg, rgb(242, 6, 95), rgb(200, 5, 80))' }}>
                </div>
              </div>
              <p className="text-2xl font-bold text-white uppercase tracking-wide leading-tight">
                Accommodation<br/>Available
              </p>
              <div className="absolute inset-0 bg-gradient-to-br from-pink-600/0 to-pink-600/0 group-hover:from-pink-600/5 group-hover:to-pink-600/10 transition-all duration-300"></div>
            </div>

            {/* Free Airfare */}
            <div className="group relative overflow-hidden rounded-2xl p-8 text-center transition-all duration-300 bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 hover:border-pink-600">
              <div className="mb-6">
                <div className="text-6xl font-black text-transparent bg-clip-text mb-2" 
                  style={{ backgroundImage: 'linear-gradient(135deg, rgb(242, 6, 95), rgb(200, 5, 80))' }}>
                  FREE
                </div>
                <div className="h-1 w-24 mx-auto rounded-full" 
                  style={{ background: 'linear-gradient(90deg, rgb(242, 6, 95), rgb(200, 5, 80))' }}>
                </div>
              </div>
              <p className="text-2xl font-bold text-white uppercase tracking-wide leading-tight">
                Airfare
              </p>
              <div className="absolute inset-0 bg-gradient-to-br from-pink-600/0 to-pink-600/0 group-hover:from-pink-600/5 group-hover:to-pink-600/10 transition-all duration-300"></div>
            </div>

            {/* No Experience Required */}
            <div className="group relative overflow-hidden rounded-2xl p-8 text-center transition-all duration-300 bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 hover:border-pink-600">
              <div className="mb-6">
                <div className="text-6xl font-black text-transparent bg-clip-text mb-2" 
                  style={{ backgroundImage: 'linear-gradient(135deg, rgb(242, 6, 95), rgb(200, 5, 80))' }}>
                  NO
                </div>
                <div className="h-1 w-24 mx-auto rounded-full" 
                  style={{ background: 'linear-gradient(90deg, rgb(242, 6, 95), rgb(200, 5, 80))' }}>
                </div>
              </div>
              <p className="text-2xl font-bold text-white uppercase tracking-wide leading-tight">
                Experience<br/>Required!
              </p>
              <div className="absolute inset-0 bg-gradient-to-br from-pink-600/0 to-pink-600/0 group-hover:from-pink-600/5 group-hover:to-pink-600/10 transition-all duration-300"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 bg-black">
        <div className="container mx-auto max-w-6xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 text-white leading-tight">
            There's an opening spot to join our <span style={{ color: 'rgb(242, 6, 95)' }}>prestigious establishment</span> - one of the <span style={{ color: 'rgb(242, 6, 95)' }}>best jobs</span> in Sydney & Melbourne.
          </h2>
          
          <p className="text-xl md:text-2xl leading-relaxed mb-8 text-gray-200 max-w-5xl mx-auto">
            New employees are warmly welcomed by our experienced and friendly management team with training and advice to support your <span style={{ color: 'rgb(242, 6, 95)', fontWeight: '600' }}>successful high-paying career</span> in the industry.
          </p>
          
          <p className="text-xl md:text-2xl leading-relaxed text-gray-200 max-w-5xl mx-auto">
            We let you enjoy life on your own terms, set your own hours, and meet new people. This is your chance to earn <span style={{ color: 'rgb(242, 6, 95)', fontWeight: '600' }}>great money</span> in a safe environment and <span style={{ color: 'rgb(242, 6, 95)', fontWeight: '600' }}>classy establishment</span>.
          </p>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-16 px-4 bg-black">
        <div className="container mx-auto max-w-5xl">
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src="https://d9hhrg4mnvzow.cloudfront.net/www.highpaidnoexperiencejobs.com.au/8834f2e1-14-luxury-item-1024x1024px_10dc0dc000000000000000.jpg" 
              alt="Luxury lifestyle" 
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Who We're Looking For */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
            We are looking for motivated employees <span style={{ color: 'rgb(242, 6, 95)' }}>between the ages of 18 and 35</span> who want to be the best they can be and focus on achieving their personal goals.
          </h2>
          
          <p className="text-xl md:text-2xl leading-relaxed text-gray-700 mb-8">
            You could be a traveller, a student, a single mum, backpacker, dancer or anyone trying to get ahead in life.
          </p>
          
          <p className="text-2xl md:text-3xl font-bold" style={{ color: 'rgb(242, 6, 95)' }}>
            NO EXPERIENCE HIGH PAYING JOBS FOR THE RIGHT CANDIDATES. APPLY FOR INTERVIEW.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-900">
            We'll Have You Looking — and Feeling — Like a Million Bucks!
          </h2>

          <div className="max-w-5xl mx-auto space-y-8 text-lg leading-relaxed text-gray-700">
            <p>
              <strong className="text-gray-900">Flying in from out of town?</strong> No stress — we've got your back. Whether you're coming from interstate or just outside Sydney or Melbourne, we'll cover your flights, reimburse your travel, and even sort out comfy off-site accommodation. Easy as!
            </p>
            
            <p>
              <strong className="text-gray-900">Need a little glam boost?</strong> You'll love our generous perks for clothing, hair, makeup, and beauty — because when you look good, you feel unstoppable.
            </p>
            
            <p>
              If you're new to the scene, don't worry — we're here to help you every step of the way. We'll give you free support with hair, makeup, fitness, savings, diet, and nutrition — plus help picking out the perfect outfit and shoes. It's all about helping you feel confident, polished, and totally client-ready.
            </p>
            
            <p>
              Oh, and did we mention? Our hairstylist, makeup artist, and personal trainer are available by appointment to help you bring your A-game whenever you need a little extra sparkle.
            </p>
          </div>

          {/* Icons Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            <div className="text-center">
              <div className="rounded-full w-32 h-32 mx-auto mb-6 flex items-center justify-center shadow-xl"
                style={{ background: 'linear-gradient(to bottom right, rgb(242, 6, 95), rgb(200, 5, 80))' }}>
                <img 
                  src="https://d9hhrg4mnvzow.cloudfront.net/www.highpaidnoexperiencejobs.com.au/56024ff3-195445_1000000000000000000028.png" 
                  alt="Accommodation" 
                  className="w-20 h-20 object-contain"
                />
              </div>
              <p className="text-2xl font-bold text-gray-900">FREE</p>
              <p className="text-xl text-gray-700">ACCOMMODATION AVAILABLE</p>
            </div>

            <div className="text-center">
              <div className="rounded-full w-32 h-32 mx-auto mb-6 flex items-center justify-center shadow-xl"
                style={{ background: 'linear-gradient(to bottom right, rgb(242, 6, 95), rgb(200, 5, 80))' }}>
                <img 
                  src="https://d9hhrg4mnvzow.cloudfront.net/www.highpaidnoexperiencejobs.com.au/gmii4d-email_100t00t000000000000028.png" 
                  alt="Airfare" 
                  className="w-20 h-20 object-contain"
                />
              </div>
              <p className="text-2xl font-bold text-gray-900">FREE</p>
              <p className="text-xl text-gray-700">AIRFARE</p>
            </div>

            <div className="text-center">
              <div className="rounded-full w-32 h-32 mx-auto mb-6 flex items-center justify-center shadow-xl text-white text-5xl font-bold"
                style={{ background: 'linear-gradient(to bottom right, rgb(242, 6, 95), rgb(200, 5, 80))' }}>
                ✓
              </div>
              <p className="text-2xl font-bold text-gray-900">NO EXPERIENCE</p>
              <p className="text-xl text-gray-700">REQUIRED!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 bg-gray-900 text-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Interested? – Find Out More Today
          </h2>
          <p className="text-xl md:text-2xl mb-12 text-gray-300">(Confidentiality Assured)</p>
          
          <div className="max-w-4xl mx-auto space-y-8 text-lg leading-relaxed">
            <p>
              If you're not quite sure what to expect, or you have any questions whatsoever, please SMS Lisa: <strong className="text-2xl" style={{ color: 'rgb(242, 6, 95)' }}>0429 085683</strong> (no direct telephone calls) or email <a href="mailto:info@anytimelawyers.com" className="hover:underline font-semibold" style={{ color: 'rgb(242, 6, 95)' }}>info@anytimelawyers.com</a> and we will be glad to answer your questions in complete confidentiality.
            </p>
            
            <p className="font-semibold">
              <strong>IMPORTANT:</strong> if you are currently living interstate, we will gladly cover your airfares to join us for an interview in Sydney & Melbourne after we have spoken on the telephone.
            </p>
            
            <p className="text-xl font-semibold" style={{ color: 'rgb(242, 6, 95)' }}>
              You may be invited to start as soon as possible.
            </p>
            
            <p className="text-lg">
              We look forward to hearing from you.
            </p>

            <a 
              href="#top"
              className="inline-block mt-8 px-12 py-4 hover:text-white font-bold text-xl rounded-full transition-all duration-300"
              style={{ 
                borderWidth: '3px',
                borderColor: 'rgb(242, 6, 95)',
                color: 'rgb(242, 6, 95)',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgb(242, 6, 95)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              APPLY NOW
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          <div className="mb-6">
            <img 
              src="https://d9hhrg4mnvzow.cloudfront.net/www.highpaidnoexperiencejobs.com.au/82372698-nehpj_105a02v000000000000028.png" 
              alt="No Experience High Paying Jobs Logo" 
              className="h-16 mx-auto mb-6"
            />
          </div>
          
          <p className="text-3xl font-bold mb-4" style={{ color: 'rgb(242, 6, 95)' }}>SMS Lisa: 0429 085683</p>
          
          <div className="my-8">
            <p className="text-2xl font-bold mb-2" style={{ color: 'rgb(242, 6, 95)' }}>INCOME RANGE</p>
            <p className="text-3xl font-bold mb-4">$3000 – $6000 PER WEEK</p>
            <div className="space-y-2 text-lg">
              <p>- Full Training Will Be Provided.</p>
              <p>- All Female Friendly Management.</p>
              <p>- No Experience Required - Age limit 18-35</p>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-800">
            <p className="text-sm text-gray-400">© 2026 HIGH PAID NO EXPERIENCE</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;